/**
 * authRedirect.js — the one redirect-to-login path, shared by BOTH API clients.
 *
 * A hard redirect to /login is a full page reload, so anything that re-triggers
 * it during load spins forever. `services/apiClient.js` (httpOnly-cookie auth)
 * and the legacy `services/api.js` (localStorage tokens) each hit this path on
 * a 401, and `services/api.js` is imported by ~27 modules including the
 * dashboards — so the breaker has to be SHARED. Two independent counters would
 * each stay under their own limit while a loop alternating between the clients
 * reloaded the page indefinitely.
 *
 * The window log lives in sessionStorage precisely because it must survive the
 * reload it is trying to detect; per-tab is the right scope, since the loop is
 * per-tab.
 */

const REDIRECT_LOG_KEY = "vitrine_auth_redirects";
const REDIRECT_LIMIT = 3;
const REDIRECT_WINDOW_MS = 10_000;

/**
 * Send the user to /login, unless doing so would be a loop.
 *
 * @returns {boolean} true if the redirect was issued, false if suppressed.
 */
export function redirectToLogin() {
  // Never redirect to the page we are already on — that alone is a reload loop.
  if (window.location.pathname === "/login") return false;

  try {
    const now = Date.now();
    const recent = JSON.parse(sessionStorage.getItem(REDIRECT_LOG_KEY) || "[]")
      .filter((t) => now - t < REDIRECT_WINDOW_MS);

    if (recent.length >= REDIRECT_LIMIT) {
      console.error(
        `[auth] ${recent.length} redirects to /login in ${REDIRECT_WINDOW_MS / 1000}s — ` +
          "stopping to avoid a reload loop. Something is calling an authenticated " +
          "endpoint without a session."
      );
      return false;
    }

    recent.push(now);
    sessionStorage.setItem(REDIRECT_LOG_KEY, JSON.stringify(recent));
  } catch {
    /* private mode / storage disabled — fall through and redirect once */
  }

  window.location.href = "/login";
  return true;
}

/**
 * True for the endpoints whose own 401 means "wrong credentials", not "your
 * session expired". Running the refresh-then-redirect path on these reloads the
 * page and wipes the form instead of showing the invalid-credentials message.
 */
export function isCredentialCheck(url) {
  return /\/auth\/(login|mfa\/login-verify)(\?|$)/.test(url || "");
}

export { REDIRECT_LOG_KEY, REDIRECT_LIMIT, REDIRECT_WINDOW_MS };
